import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useOrdererProjects } from '../../hooks/useOrdererProjects';
import { useOrdererOrders } from '../../hooks/useOrdererOrders';
import { useInvoices } from '../../hooks/useInvoice';
import Navbar from '../../components/common/Navbar';
import AddressInput from '../../components/common/AddressInput';
import SheetModal, { sheetButtons } from '../../components/common/SheetModal';
import { COLORS, FONTS } from '../../styles/tokens';

const EMPTY_ADDR = { street: '', city: '', lat: null, lng: null };

export default function ProjectsPage() {
  const { currentUser } = useAuth();
  const { projects, loading } = useOrdererProjects();
  const { orders } = useOrdererOrders();
  const { invoices } = useInvoices();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [name,    setName]    = useState('');
  const [desc,    setDesc]    = useState('');
  const [address, setAddress] = useState(EMPTY_ADDR);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  // Antall oppdrag og samlet forbruk (fakturert) per prosjekt
  const stats = useMemo(() => {
    const projectOfOrder = new Map(orders.map(o => [o.id, o.projectId]));
    const map = new Map();
    orders.forEach(o => {
      if (!o.projectId) return;
      const s = map.get(o.projectId) ?? { count: 0, spend: 0 };
      s.count += 1;
      map.set(o.projectId, s);
    });
    invoices.forEach(inv => {
      const pid = projectOfOrder.get(inv.orderId);
      if (!pid) return;
      const s = map.get(pid) ?? { count: 0, spend: 0 };
      s.spend += inv.totalAmount ?? 0;
      map.set(pid, s);
    });
    return map;
  }, [orders, invoices]);

  function closeForm() {
    if (saving) return;
    setShowForm(false);
    setError('');
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Angi prosjektnavn'); return; }
    setSaving(true);
    setError('');
    try {
      const ref = await addDoc(collection(db, 'projects'), {
        ordererUid:  currentUser.uid,
        name:        name.trim(),
        description: desc.trim(),
        address:     address.street ? address : null,
        createdAt:   serverTimestamp(),
      });
      setName('');
      setDesc('');
      setAddress(EMPTY_ADDR);
      setShowForm(false);
      navigate(`/orderer/projects/${ref.id}`);
    } catch {
      setError('Kunne ikke opprette prosjekt. Prøv igjen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
        <h1 style={styles.title}>Prosjekter</h1>

        {loading ? (
          <p style={styles.state}>Laster prosjekter…</p>
        ) : projects.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyTitle}>Ingen prosjekter ennå</div>
            <p style={styles.emptyText}>Samle oppdrag under et prosjekt for enkel oversikt og felles faktura.</p>
          </div>
        ) : (
          projects.map(p => {
            const s = stats.get(p.id) ?? { count: 0, spend: 0 };
            return (
              <button key={p.id} type="button" style={styles.row} onClick={() => navigate(`/orderer/projects/${p.id}`)}>
                <span style={styles.icon} aria-hidden="true">
                  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l9 5-9 5-9-5z" /><path d="M3 13l9 5 9-5" /></svg>
                </span>
                <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <span style={styles.rowName}>{p.name}</span>
                  <span style={styles.rowMono}>{s.count} {s.count === 1 ? 'OPPDRAG' : 'OPPDRAG'}</span>
                </span>
                <span style={styles.rowSpend}>{s.spend > 0 ? s.spend.toLocaleString('nb-NO') : '0'}</span>
              </button>
            );
          })
        )}

        <button className="tippi-btn" style={styles.newBtn} onClick={() => setShowForm(true)}>
          + Nytt prosjekt
        </button>
      </div>

      {showForm && (
        <SheetModal onClose={closeForm} busy={saving} label="Opprett prosjekt">
          <form onSubmit={handleCreate}>
            <h2 style={sheetButtons.title}>Opprett prosjekt</h2>
            {error && <p role="alert" style={sheetButtons.error}>{error}</p>}
            <label style={styles.label} htmlFor="proj-name">Prosjektnavn</label>
            <input id="proj-name" className="tippi-input" style={styles.input} placeholder="F.eks. Hyttefelt Hafjell 2026"
                   value={name} onChange={e => setName(e.target.value)} autoFocus />
            <label style={styles.label} htmlFor="proj-desc">Beskrivelse (valgfritt)</label>
            <textarea id="proj-desc" className="tippi-input" style={{ ...styles.input, height: 72, padding: '12px 14px', resize: 'none' }}
                      placeholder="Kort beskrivelse av prosjektet" value={desc} onChange={e => setDesc(e.target.value)} maxLength={300} />
            <AddressInput label="Adresse (valgfritt)" value={address} onChange={setAddress} />
            <button type="submit" style={{ ...sheetButtons.gold, marginTop: 16, opacity: saving ? 0.6 : 1 }} disabled={saving}>
              {saving ? 'Oppretter…' : 'Opprett prosjekt'}
            </button>
            <button type="button" style={sheetButtons.ghost} disabled={saving} onClick={closeForm}>Avbryt</button>
          </form>
        </SheetModal>
      )}
    </div>
  );
}

const styles = {
  root:      { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:      { maxWidth: 720, margin: '0 auto', padding: '24px 20px', width: '100%', paddingTop: 'max(32px, env(safe-area-inset-top))' },
  title:     { fontFamily: FONTS.display, fontSize: 40, lineHeight: 1, fontWeight: 700, margin: '12px 0 10px', color: COLORS.textDark },
  row:       { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '13px 0', background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.textDark },
  icon:      { width: 44, height: 44, borderRadius: 12, background: '#efeadd', color: '#1a1a2e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rowName:   { display: 'block', fontWeight: 700, fontSize: 15 },
  rowMono:   { display: 'block', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, marginTop: 2, letterSpacing: '0.03em' },
  rowSpend:  { fontFamily: FONTS.display, fontWeight: 700, fontSize: 24, lineHeight: 1 },
  newBtn:    { width: '100%', height: 52, borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 20 },
  label:     { display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textMid, margin: '10px 0 5px' },
  input:     { width: '100%', boxSizing: 'border-box', height: 52, padding: '0 14px', borderRadius: 14, border: `1px solid ${COLORS.border}`, fontSize: 15.5, fontFamily: 'inherit', background: COLORS.white },
  state:     { textAlign: 'center', color: COLORS.textLight, padding: 40 },
  empty:     { textAlign: 'center', padding: '48px 20px' },
  emptyTitle:{ fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, color: COLORS.textDark },
  emptyText: { color: COLORS.textMid, fontSize: 14, marginTop: 6, lineHeight: 1.45 },
};
